// RoutingGuides.jsx
import React, { useState, useEffect } from "react";
import Table from "../../Components/Table";
import { RoutingGuidesColumn } from "../../ColumnsConfig/RoutingGuidesColumn";
import { Alert, Box, Button, TextField, Grid } from "@mui/material"; // Import Grid for layout
import loaderGif from "../../Assests/loader.gif";
import "./../../App.css";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";
import {
  fetchRoutingGuides,
  createRoutingGuide,
  fetchCommodityCodes,
  deleteRoutingGuide,
  fetchDivisions,
} from "../../Services/RoutingGuides";
import ConfirmationModal from "../../Common/ConfirmationModal";
import RoutingGuideForm from "../../Modals/RoutingGuidesFormDialog";

const RoutingGuides = () => {
  // State Management
  const [filteredRows, setFilteredRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [divisions, setDivisions] = useState([]);
  const [commodityCodes, setCommodityCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [searchParams, setSearchParams] = useState({}); // State for search parameters

  // UI State
  const [loading, setLoading] = useState(false);
  const [noRecordsFound, setNoRecordsFound] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Pagination and Sorting State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortColumn] = useState("commodity_code");
  const [order, setSortOrder] = useState("asc");

  // Delete Operation State
  const [rowToDelete, setRowToDelete] = useState(null);

  // Fetch Data Functions
  const fetchRoutingGuidesData = async (
    searchParams = {},
    page,
    pageSize,
    sortBy = "",
    order = "asc"
  ) => {
    setLoading(true);
    try {
      const response = await fetchRoutingGuides(
        searchParams,
        page,
        pageSize,
        sortBy,
        order
      );
      setFilteredRows(response.data);
      setTotalRecords(response.totalRecords);
      setCurrentPage(page);
      setNoRecordsFound(response.data.length === 0);
    } catch (error) {
      NotificationManager.error("Failed to fetch routing guides");
      setNoRecordsFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommodityCodesData = async () => {
    try {
      const commodityCodesData = await fetchCommodityCodes();
      setCommodityCodes(commodityCodesData);
    } catch (error) {
      NotificationManager.error("Failed to fetch commodity codes");
      setCommodityCodes([]);
    }
  };

  const fetchDivisionsData = async () => {
    try {
      const divisionsData = await fetchDivisions();
      setDivisions(divisionsData);
    } catch (error) {
      NotificationManager.error("Failed to fetch divisions");
      setDivisions([]);
    }
  };

  // Initial Data Load
  useEffect(() => {
    fetchRoutingGuidesData({}, currentPage, pageSize);
    fetchCommodityCodesData();
    fetchDivisionsData();
  }, []);

  useEffect(() => {
    fetchRoutingGuidesData({ searchTerm: searchParams }, currentPage, pageSize,sortBy,order);
  }, [currentPage, pageSize, searchParams]); // Add searchTerm to dependencies

  // Handler Functions
  const handleAdd = async (formData) => {
    try {
      await createRoutingGuide(formData);
      NotificationManager.success("Routing guide added successfully");
      setIsDialogOpen(false);
      fetchRoutingGuidesData({}, 1, 10);
    } catch (error) {
      NotificationManager.error("Failed to add routing guide");
      console.error(error);
    }
  };

  const handleSort = (column, order) => {
    setSortColumn(column);
    setSortOrder(order);
    fetchRoutingGuidesData({}, currentPage, pageSize, column, order);
  };

  const handleDelete = (rowData) => {
    setRowToDelete(rowData);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteRoutingGuide(rowToDelete.id);
      NotificationManager.success(
        "RoutingGuide deleted successfully.",
        "Success",
        3000
      );
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
      fetchRoutingGuidesData({}, 1, 10);
    } catch (error) {
      handleDeleteError(error);
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const handleDeleteError = (error) => {
    if (error) {
      NotificationManager.warning(
        "Please check your inputs.",
        "Bad Request"`${error}`,
        5000
      );
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handlePageChange = (page, newPageSize,sortBy,order) => {
    setCurrentPage(page);
    setPageSize(newPageSize || pageSize);
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const handleSearchButtonClick = async () => {
    try {
      setSearchParams(searchTerm);

        fetchRoutingGuidesData({ searchTerm: searchTerm }, currentPage, pageSize);
    } catch (error) {
      console.error("Error fetching routing guides:", error);
      NotificationManager.error("Failed to fetch routing guides");
    }
  };

  // Render Loading Overlay
  const renderLoadingOverlay = () => {
    if (!loading) return null;

    return (
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="rgba(255, 255, 255, 0.8)"
        zIndex={1}
      >
        <img src={loaderGif} alt="Loading..." width={50} height={50} />
      </Box>
    );
  };

  // Render No Records Message
  const renderNoRecordsMessage = () => {
    if (!noRecordsFound || loading) return null;

    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No records found for the selected filters.
      </Alert>
    );
  };

  return (
    <div className="px-5 my-1">
      <NotificationContainer />

      {/* Routing Guide Form Dialog */}
      <RoutingGuideForm
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleAdd}
        divisions={divisions}
        commodityCodes={commodityCodes}
      />

      {/* Main Content */}
      <Box position="relative" mt={2}>
        {/* Search Bar and Button */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={9}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              size="small" // Makes the TextField smaller
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  width: "100%",
                   
                },
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="contained"
              onClick={handleSearchButtonClick}
              fullWidth
              sx={{
                height: "40px", // Match the height of the TextField
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {renderLoadingOverlay()}
        {renderNoRecordsMessage()}

        <Table
          rows={filteredRows}
          columns={RoutingGuidesColumn(handleDelete)}
          paginationMode="server"
          currentPage={currentPage}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={handlePageChange}
          onSort={handleSort}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>

      {/* Add Button */}
      <Button
        variant="contained"
        onClick={handleDialogOpen}
        className="add-new-btn"
      >
        Add New Routing Guide
      </Button>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to inactivate this RoutingGuide?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default RoutingGuides;
